/**
 * SendContractDialog — invio contratto da matrice (★ Fase 8).
 * Amministrazione + Senior. Compila i campi editabili, anteprima, invia:
 * il cliente riceve email con link di firma sicuro.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Send, Eye, Copy, Paperclip, X, FileText, Lock } from 'lucide-react';
import { renderWithValues } from './contractUtils';
import { ClientPreviewDialog } from './ClientPreviewDialog';
import { useAuth } from '../../lib/auth-context';

const MAX_ATTACHMENTS = 5;
const MAX_ATT_SIZE = 5 * 1024 * 1024; // 5 MB

function fmtSize(bytes) {
  if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function SendContractDialog({ open, onClose, onSent, lead }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [values, setValues] = useState({});
  const [attachments, setAttachments] = useState([]); // {name, mime, b64, size}
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTemplateId('');
    setValues({});
    setAttachments([]);
    setPreview(false);
    setRecipientName(lead?.name || '');
    setRecipientEmail(lead?.email || '');
    api.get('/contracts/templates')
      .then((r) => setTemplates((r.data || []).filter((t) => t.active !== false)))
      .catch(() => { setTemplates([]); toast.error(t('sendContract.templatesLoadError', 'Errore caricamento matrici')); });
  }, [open, lead]);

  const tpl = useMemo(() => (templates || []).find((t) => t.id === templateId), [templates, templateId]);
  // ★ Fase 12 — allegati predefiniti della matrice: fissi, non rimovibili
  const tplAtts = useMemo(() => (tpl?.attachments || []), [tpl]);
  const previewHtml = useMemo(() => (tpl ? renderWithValues(tpl.content_html, values) : ''), [tpl, values]);
  const missingRequired = useMemo(
    () => (tpl?.fields || []).filter((f) => f.required && !(values[f.key] || '').trim()),
    [tpl, values],
  );
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim());
  const canSend = tpl && emailOk && recipientName.trim().length >= 2 && missingRequired.length === 0;

  const onPickAttachments = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      toast.error(t('sendContract.maxAttachments', { defaultValue: 'Massimo {{max}} allegati per contratto.', max: MAX_ATTACHMENTS }));
      return;
    }
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    for (const file of files) {
      if (!allowed.includes(file.type)) {
        toast.error(t('sendContract.unsupportedFormat', { defaultValue: '«{{name}}»: formato non supportato (PDF o immagini).', name: file.name }));
        continue;
      }
      if (file.size > MAX_ATT_SIZE) {
        toast.error(t('sendContract.fileTooLarge', { defaultValue: '«{{name}}» troppo grande (max 5 MB).', name: file.name }));
        continue;
      }
      try {
        const b64 = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(String(fr.result).split(',')[1]);
          fr.onerror = reject;
          fr.readAsDataURL(file);
        });
        setAttachments((s) => [...s, { name: file.name, mime: file.type, b64, size: file.size }]);
      } catch {
        toast.error(t('sendContract.readError', { defaultValue: 'Impossibile leggere «{{name}}».', name: file.name }));
      }
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/contracts/send', {
        template_id: templateId,
        recipient_name: recipientName.trim(),
        recipient_email: recipientEmail.trim(),
        lead_id: lead?.id || null,
        values,
        attachments: attachments.map((a) => ({ name: a.name, mime: a.mime, content_b64: a.b64 })),
      }, { timeout: 120000 });
      const localLink = `${window.location.origin}${data.sign_path}`;
      toast.success(t('sendContract.sentOk', 'Contratto inviato via email!'), {
        description: t('sendContract.sentOkDesc', 'Link firma copiabile dalla lista contratti.'),
        action: {
          label: t('sendContract.copyLink', 'Copia link'),
          onClick: () => navigator.clipboard.writeText(localLink).then(() => toast.success(t('sendContract.linkCopied', 'Link copiato'))),
        },
      });
      onSent?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('sendContract.sendError', 'Errore invio contratto'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="send-contract-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[hsl(var(--primary))]" /> {t('sendContract.title', 'Invia contratto')}
            {lead && <span className="text-sm font-normal text-muted-foreground">· {lead.name}</span>}
          </DialogTitle>
          <DialogDescription>
            {t('sendContract.desc', 'Il cliente riceverà un link sicuro: verifica email (OTP), documento + selfie e firma elettronica.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide">{t('sendContract.template', 'Matrice')} *</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="mt-1" data-testid="send-template-select">
                <SelectValue placeholder={templates === null ? t('sendContract.loading', 'Caricamento…') : (templates.length ? t('sendContract.selectTemplate', 'Seleziona la matrice…') : t('sendContract.noTemplates', 'Nessuna matrice: creala nella sezione Contratti'))} />
              </SelectTrigger>
              <SelectContent>
                {(templates || []).map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`send-template-opt-${t.id}`}>
                    {t.name}{t.description ? ` — ${t.description}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">{t('sendContract.signerName', 'Nome firmatario')} *</Label>
              <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className="mt-1" data-testid="send-recipient-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">{t('sendContract.signerEmail', 'Email firmatario')} *</Label>
              <Input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} className="mt-1" data-testid="send-recipient-email" />
            </div>
          </div>

          {tpl && (
            <div>
              <Label className="text-xs uppercase tracking-wide">{t('sendContract.contractFields', 'Campi del contratto')}</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mt-2" data-testid="send-fields-form">
                {(tpl.fields || []).map((f) => (
                  <div key={f.key}>
                    <Label className="text-[11px] text-muted-foreground">
                      {f.label} {f.required && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      value={values[f.key] || ''}
                      onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                      className="mt-0.5 h-9"
                      data-testid={`send-field-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {tpl && (
            <div>
              <Label className="text-xs uppercase tracking-wide">
                {t('sendContract.attachments', 'Allegati')} <span className="normal-case text-muted-foreground">{t('sendContract.attachmentsHint', '(PDF o immagini)')}</span>
              </Label>
              <div className="mt-2 space-y-2" data-testid="send-attachments">
                {/* ★ Fase 12 — allegati predefiniti della matrice (fissi) */}
                {tplAtts.map((a, i) => (
                  <div
                    key={a.id || `tpl-${i}`}
                    className="flex items-center gap-2 rounded-md border border-[var(--gp-gold)]/50 bg-[var(--gp-gold)]/10 px-2.5 py-1.5"
                    data-testid={`send-tpl-attachment-${i}`}
                  >
                    <Lock className="w-3.5 h-3.5 text-[hsl(var(--primary))] shrink-0" />
                    <FileText className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
                    <span className="text-xs truncate flex-1">{a.name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0 italic">
                      {t('sendContract.fromTemplate', 'dalla matrice · sempre incluso')}
                    </span>
                  </div>
                ))}
                {attachments.map((a, i) => (
                  <div key={`${a.name}-${i}`} className="flex items-center gap-2 rounded-md border border-border bg-[var(--surface-2)]/60 px-2.5 py-1.5" data-testid={`send-attachment-${i}`}>
                    <FileText className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
                    <span className="text-xs truncate flex-1">{a.name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{fmtSize(a.size)}</span>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700 shrink-0"
                      onClick={() => setAttachments((s) => s.filter((_, j) => j !== i))}
                      data-testid={`send-attachment-remove-${i}`}
                      aria-label={`${t('sendContract.remove', 'Rimuovi')} ${a.name}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {attachments.length < MAX_ATTACHMENTS && (
                  <label className="flex items-center gap-2 rounded-md border-2 border-dashed border-border hover:border-[hsl(var(--primary))]/40 px-3 py-2.5 cursor-pointer text-xs text-muted-foreground">
                    <Paperclip className="w-4 h-4" />
                    {t('sendContract.addExtra', { defaultValue: 'Aggiungi allegati extra (max {{max}} — es. Contratto sociale SPV, Privacy…)', max: MAX_ATTACHMENTS })}
                    <input
                      type="file" multiple className="hidden"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      onChange={onPickAttachments}
                      data-testid="send-attachments-input"
                    />
                  </label>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                {tplAtts.length > 0 && (
                  <>{t('sendContract.tplAttsFixed', 'Gli allegati della matrice sono fissi e verranno sempre inviati.')} </>
                )}
                {t('sendContract.signerCanRead', 'Il firmatario potrà leggerli e scaricarli nella pagina di firma, prima di firmare.')}
              </p>
            </div>
          )}

          {tpl && (
            <div>
              <Button size="sm" variant="outline" className="h-9 text-xs w-full" onClick={() => setPreview(true)} data-testid="send-preview-toggle">
                <Eye className="w-3.5 h-3.5 mr-1.5" />
                {t('sendContract.previewClient', 'Anteprima cliente — vedi il contratto come lo vedrà il firmatario')}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={sending}>{t('sendContract.cancel', 'Annulla')}</Button>
          <Button
            onClick={send}
            disabled={!canSend || sending}
            data-testid="send-contract-btn"
            className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {t('sendContract.sendForSignature', 'Invia per la firma')}
          </Button>
        </DialogFooter>

        {/* ★ Fase 8.W — Anteprima fedele della pagina di firma */}
        <ClientPreviewDialog
          open={preview}
          onClose={() => setPreview(false)}
          templateName={tpl?.name}
          recipientName={recipientName.trim()}
          senderName={user?.name}
          contentHtml={previewHtml}
          attachments={[...tplAtts, ...attachments]}
        />
      </DialogContent>
    </Dialog>
  );
}

export function CopyLinkButton({ signPath }) {
  return (
    <Button
      size="sm" variant="ghost" className="h-7 px-2"
      onClick={() => {
        navigator.clipboard.writeText(`${window.location.origin}${signPath}`)
          .then(() => toast.success(i18n.t('sendContract.signLinkCopied', 'Link firma copiato')));
      }}
      title={i18n.t('sendContract.copySignLink', 'Copia link firma')}
    >
      <Copy className="w-3.5 h-3.5" />
    </Button>
  );
}
